import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import dayjs from 'dayjs';
import { useForm, Controller } from 'react-hook-form';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import InputAdornment from '@mui/material/InputAdornment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import { Autocomplete } from '@mui/material';
import { useGetGroupBankAccQuery } from '../../services/matchStatement';
import { CircularProgress } from '@mui/material';


function SearchStatement({
  statusItems,
  detailStatusItems,
  formDefaultData,
  setFormDefaultData,
  merchant,
  setPage,
  callApiGetStatementOnline,
  t,
 onExportExcel, // ✅ รับฟังก์ชัน
  isExporting,   // ✅ รับสถานะ
  exportProgress,
}) {
  const [formFilter, _] = useState(formDefaultData);
  const { data: bankAll } = useGetGroupBankAccQuery();
  // console.log('🚀 ~ bankAll:', bankAll?.data);
  const [dateAll, setDateAll] = useState({
    startDate: formFilter.startDate,
    endDate: formFilter.endDate,
    startEffectiveDate: null,
    endEffectiveDate: null,
  });
  const [selectedBank, setSelectedBank] = useState(null);
  const [selectedAccountNo, setSelectedAccountNo] = useState(null);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [filteredBanks, setFilteredBanks] = useState([]);

  useEffect(() => {
    if (bankAll?.data) {
      let accounts = bankAll.data;
      let banks = bankAll.data;

      if (selectedBank) {
        accounts = accounts.filter((acc) => acc.bank_name === selectedBank);
      }

      if (selectedAccountNo) {
        const matchedAccount = bankAll.data.find(
          (acc) => acc.account_no === selectedAccountNo,
        );
        if (matchedAccount) {
          banks = [matchedAccount.bank_name];
          setSelectedBank(matchedAccount.bank_name); // 🎯 อัปเดต Bank อัตโนมัติ
        }
      } else {
        banks = [...new Set(bankAll.data.map((acc) => acc.bank_name))];
      }

      setFilteredAccounts(accounts);
      setFilteredBanks(banks);
    }
  }, [selectedBank, selectedAccountNo, bankAll]);

  // useEffect(() => {
  //   if (bankAll?.data) {
  //     // ดึงข้อมูล bank_name ที่ไม่ซ้ำ
  //     const uniqueBanks = [
  //       ...new Set(bankAll.data.map((item) => item.bank_name)),
  //     ];
  //     setBankValue(uniqueBanks);

  //     const uniqueAccount = bankAll.data.map((item) => item.account_no);
  //     setAccountNo(uniqueAccount);
  //   }
  // }, [bankAll]);

  const { control, handleSubmit, reset: resetForm } = useForm();
  const onSubmit = async (data) => {
    const nextFilterData = {
      ...data,
      bank: selectedBank || data.bank || '',
      accountNo: selectedAccountNo || data.accountNo || '',
      status: data.status ? data.status.label : '',
      detailStatus: data.detailStatus
        ? data.detailStatus.value || data.detailStatus.label
        : '',
      sorgcode: merchant || '',
    };

    setPage(0);
    callApiGetStatementOnline(nextFilterData); // เรียกฟังก์ชันที่ส่งมาจาก parent
  };

  const handleReset = async () => {
    resetForm({
      bank: null,
      accountNo: null,
      status: null,
      detailStatus: null,
      startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
      startEffectiveDate: '',
      endEffectiveDate: '',
      creditAmount: '',
      debitAmount: '',
      search: '',
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="px-4 pt-6 pb-4 border-0  md:border-2 border-gray-200 rounded-md grid grid-cols-1 xl:grid-cols-4 gap-x-4 gap-y-2">
        <div>
          {t('app.bank')}
          <div className="mt-2">
            <Controller
              name="bank"
              control={control}
              defaultValue={selectedBank}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  size="small"
                  options={filteredBanks || []}
                  getOptionLabel={(option) => option || ''}
                  isOptionEqualToValue={(option, value) => option === value}
                  value={selectedBank || null}
                  onChange={(_, data) => {
                    setSelectedBank(data);
                    field.onChange(data);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t('app.selectBank')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start" sx={{ mr: 1 }}>
                            <AccountBalanceIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </div>
        </div>

        <div>
          {t('app.accountNo')}
          <div className="mt-2">
            <Controller
              name="accountNo"
              control={control}
              defaultValue={selectedAccountNo}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  size="small"
                  options={filteredAccounts || []}
                  getOptionLabel={(option) => option.account_no || ''}
                  isOptionEqualToValue={(option, value) =>
                    option.account_no === value?.account_no
                  }
                  value={
                    selectedAccountNo ? { account_no: selectedAccountNo } : null
                  }
                  onChange={(_, data) => {
                    if (data) {
                      setSelectedAccountNo(data?.account_no || null);
                      setSelectedBank(data?.bank_name || null); // 🎯 อัปเดต Bank อัตโนมัติ
                      field.onChange(data?.account_no || null);
                    } else {
                      setSelectedAccountNo(null);
                      field.onChange(null);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t('app.selelctAccountNo')} // ✅ แก้ไข
                    />
                  )}
                />
              )}
            />
          </div>
        </div>

        <div>
          {t('app.status')} {/* ✅ แก้ไข */}
          <div className="mt-2">
            <Controller
              name="status"
              control={control}
              defaultValue={null}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  size="small"
                  options={statusItems}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.label
                  }
                  onChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t('app.selectStatus')} // ✅ แก้ไข
                    />
                  )}
                />
              )}
            />
          </div>
        </div>
        <div>
          {t('app.detailStatus')} {/* ✅ แก้ไข */}
          <div className="mt-2">
            <Controller
              name="detailStatus"
              control={control}
              defaultValue={null}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  size="small"
                  options={detailStatusItems}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.label
                  }
                  onChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t('app.selectStatus')} // ✅ แก้ไข
                    />
                  )}
                />
              )}
            />
          </div>
        </div>
        <div>
          {t('app.startDate')} {/* ✅ แก้ไข */}
          <div>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <Controller
                  name="startDate"
                  control={control}
                  defaultValue={formFilter.startDate}
                  render={({ field }) => (
                    <DatePicker
                      views={['year', 'month', 'day']}
                      {...field}
                      format="YYYY-MM-DD"
                      value={field.value ? dayjs(field.value) : null}
                      sx={{ width: '100%', backgroundColor: '#fff' }}
                      slotProps={{ textField: { size: 'small' } }}
                      maxDate={dateAll.endDate ? dayjs(dateAll.endDate) : null}
                      onChange={(newValue) => {
                        field.onChange(dayjs(newValue).format('YYYY-MM-DD'));
                        setDateAll({
                          ...dateAll,
                          startDate: newValue,
                        });
                      }}
                    />
                  )}
                />
              </DemoContainer>
            </LocalizationProvider>
          </div>
        </div>
        <div>
          {t('app.endDate')} {/* ✅ แก้ไข */}
          <div>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <Controller
                  name="endDate"
                  control={control}
                  defaultValue={formFilter.endDate}
                  render={({ field }) => (
                    <DatePicker
                      views={['year', 'month', 'day']}
                      {...field}
                      format="YYYY-MM-DD"
                      value={field.value ? dayjs(field.value) : null}
                      sx={{ width: '100%', backgroundColor: '#fff' }}
                      slotProps={{ textField: { size: 'small' } }}
                      minDate={
                        dateAll.startDate ? dayjs(dateAll.startDate) : null
                      }
                      onChange={(newValue) => {
                        field.onChange(dayjs(newValue).format('YYYY-MM-DD'));
                        setDateAll({
                          ...dateAll,
                          endDate: newValue,
                        });
                      }}
                    />
                  )}
                />
              </DemoContainer>
            </LocalizationProvider>
          </div>
        </div>

        <div>
          {t('app.effectiveStartDate')} {/* ✅ แก้ไข */}
          <div className="">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <Controller
                  name="startEffectiveDate"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <DatePicker
                      views={['year', 'month', 'day']}
                      {...field}
                      format="YYYY-MM-DD"
                      value={field.value ? dayjs(field.value) : null}
                      sx={{ width: '100%', backgroundColor: '#fff' }}
                      slotProps={{ textField: { size: 'small' } }}
                      maxDate={
                        dateAll.endEffectiveDate
                          ? dayjs(dateAll.endEffectiveDate)
                          : null
                      }
                      onChange={(newValue) => {
                        field.onChange(dayjs(newValue).format('YYYY-MM-DD'));
                        setDateAll({
                          ...dateAll,
                          startEffectiveDate: newValue,
                        });
                      }}
                    />
                  )}
                />
              </DemoContainer>
            </LocalizationProvider>
          </div>
        </div>
        <div>
          {t('app.effectiveEndDate')} {/* ✅ แก้ไข */}
          <div className="">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <Controller
                  name="endEffectiveDate"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <DatePicker
                      views={['year', 'month', 'day']}
                      {...field}
                      format="YYYY-MM-DD"
                      value={field.value ? dayjs(field.value) : null}
                      sx={{ width: '100%', backgroundColor: '#fff' }}
                      slotProps={{ textField: { size: 'small' } }}
                      minDate={
                        dateAll.startEffectiveDate
                          ? dayjs(dateAll.startEffectiveDate)
                          : null
                      }
                      onChange={(newValue) => {
                        field.onChange(dayjs(newValue).format('YYYY-MM-DD'));
                        setDateAll({
                          ...dateAll,
                          endEffectiveDate: newValue,
                        });
                      }}
                    />
                  )}
                />
              </DemoContainer>
            </LocalizationProvider>
          </div>
        </div>
        <div>
          {t('app.actualReceived')} {/* ✅ แก้ไข */}
          <div className="mt-2">
            <Controller
              name="creditAmount"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  size="small"
                  sx={{ width: '100%', backgroundColor: '#FFFFFF' }}
                  placeholder={t('app.thb')} // ✅ แก้ไข
                />
              )}
            />
          </div>
        </div>
        <div>
          {t('app.actualPaid')} {/* ✅ แก้ไข */}
          <div className="mt-2">
            <Controller
              name="debitAmount"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  size="small"
                  sx={{ width: '100%', backgroundColor: '#FFFFFF' }}
                  placeholder={t('app.thb')} // ✅ แก้ไข
                />
              )}
            />
          </div>
        </div>
        <div>
          {t('app.search')} {/* ✅ แก้ไข */}
          <div className="mt-2">
            <Controller
              name="search"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  sx={{ width: '100%', backgroundColor: '#FFFFFF' }}
                  placeholder={t('app.search')} // ✅ แก้ไข
                />
              )}
            />
          </div>
        </div>
        <div className="flex ">
          <div className="w-full self-end mt-2">
            <Button
              type="submit"
              variant="contained"
              sx={{ color: 'white', width: '100%' }}
              
            >
              <SearchIcon sx={{ marginRight: 1 }} />
              {t('app.search')} {/* ✅ แก้ไข */}
            </Button>
          </div>
        </div>
        <div />
        <div />
        <div />
        <div className="flex gap-4">
          <div className="md:w-1/2">
           <Button 
              variant="outlined" 
              sx={{ width: '100%' }}
              onClick={onExportExcel} 
              disabled={isExporting} 
            >
              {isExporting ? (
                <CircularProgress size={20} sx={{ marginRight: 1 }} />
              ) : (
                <DownloadIcon sx={{ marginRight: 1 }} />
              )}
              {isExporting
                ? `Exporting${exportProgress ? ` ${exportProgress}` : '...'}`
                : t('app.export')}
            </Button>
          </div>
          <div className="md:w-1/2 ">
            <Button
              variant="outlined"
              color="error"
              sx={{ width: '100%' }}
              onClick={() => {
                handleReset();
                setFormDefaultData({
                  bank: null,
                  accountNo: null,
                  status: null,
                  detailStatus: null,
                  startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
                  endDate: dayjs().format('YYYY-MM-DD'),
                  startEffectiveDate: '',
                  endEffectiveDate: '',
                  creditAmount: '',
                  debitAmount: '',
                  search: '',
                });
                setSelectedBank(null);
                setSelectedAccountNo(null);
                setPage(0);
                setDateAll({
                  startDate: formFilter.startDate,
                  endDate: formFilter.endDate,
                  startEffectiveDate: null,
                  endEffectiveDate: null,
                });
              }}
            >
              {t('app.clear')} {/* ✅ แก้ไข */}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}

export default SearchStatement;
